import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import getFirebase from "../../api/firebase";
import { userProfileQueryOptions } from "../queries/auth";
import { saveUserProfile, type UserProfile } from "../api/auth";
import type firebase from "firebase/compat/app";

interface AuthContextType {
  user: firebase.User | null;
  profile: UserProfile | null;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<{
    user: firebase.User | null;
    loading: boolean;
  }>({
    user: null,
    loading: true,
  });

  const queryClient = useQueryClient();

  // Firebase auth listener
  useEffect(() => {
    let mounted = true;

    const setupAuth = async () => {
      const firebase = await getFirebase();

      const unsubscribe = firebase.auth().onAuthStateChanged(async (firebaseUser) => {
        if (!mounted) return;

        if (firebaseUser) {
          // User logged in - save profile
          try {
            const profile = await saveUserProfile(firebaseUser);
            // Cache the profile
            queryClient.setQueryData(["auth", "profile", firebaseUser.uid], profile);
          } catch (error) {
            console.error("Error saving user profile:", error);
          }

          setAuthState({ user: firebaseUser, loading: false });
        } else {
          // User logged out - clear cache
          queryClient.clear();
          setAuthState({ user: null, loading: false });
        }
      });

      return unsubscribe;
    };

    const unsubscribePromise = setupAuth();

    return () => {
      mounted = false;
      unsubscribePromise.then((unsubscribe) => unsubscribe?.());
    };
  }, [queryClient]);

  // Profile query (only when user exists)
  const { data: profile } = useQuery({
    ...userProfileQueryOptions(authState.user?.uid || ""),
    enabled: !!authState.user,
  });

  const value: AuthContextType = {
    user: authState.user,
    profile: profile || null,
    isAuthenticated: !!authState.user,
    loading: authState.loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Helper hooks for common use cases
export function useUser() {
  const { user } = useAuth();
  return user;
}

export function useProfile() {
  const { profile } = useAuth();
  return profile;
}

export function useIsAuthenticated() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}
